import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';
import { clearPreloadCache } from '../components/common/InitialLoader';

const AuthContext = createContext({});

// Inactivity timeout in milliseconds (1 hour)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000;

// Mirror the access token into a cookie so nginx auth_request can gate
// /uploads/ static files for <img> tags that cannot send Authorization headers.
// SameSite=Strict keeps the cookie off cross-site requests (healthcare data);
// uploads are same-origin so Strict does not break the auth_request flow.
const setUploadCookie = (token) => {
  try {
    document.cookie = `ospito_token=${encodeURIComponent(token)}; path=/; SameSite=Strict${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch (e) { /* ignore */
  }
};

const clearUploadCookie = () => {
  try {
    document.cookie = 'ospito_token=; path=/; Max-Age=0';
  } catch (e) { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const logout = useCallback(() => {
    // Notify the backend to revoke the refresh token and end the session.
    // Fire-and-forget — we clear local state regardless so the user is
    // logged out immediately even if the network call fails.
    try {
      authAPI.logout().catch(() => {});
    } catch (e) { /* ignore */ }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    clearUploadCookie();
    clearPreloadCache(); // Clear cached data on logout
    // Clear all tenant-scoped organization caches
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ospito_organization_cache')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) { /* ignore */ }

    // Clear login handoff data so the next /login visit starts blank
    try {
      sessionStorage.removeItem('pendingLogin');
      // Non-demo users shouldn't be shown demo credentials on the login form after logout
      const isDemoUser = (user?.email?.endsWith('@demo.ospito.local')) || !!user?.is_demo_tenant;
      if (!isDemoUser) {
        sessionStorage.removeItem('demoData');
      }
    } catch (e) { /* ignore */ }

    // Clear in-memory DataCacheContext cache
    window.dispatchEvent(new Event('clearDataCache'));

    setUser(null);
  }, [user]);

  // Setup inactivity tracking
  useEffect(() => {
    if (!user) return;

    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttled activity handler to avoid excessive updates
    let throttleTimeout = null;
    const handleActivity = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        resetInactivityTimer();
        throttleTimeout = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check for inactivity every minute
    inactivityTimerRef.current = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      if (inactiveTime >= INACTIVITY_TIMEOUT) {
        
        logout();
        // Redirect to login with message
        window.location.href = '/login?reason=inactivity';
      }
    }, 60000); // Check every minute

    return () => {
      // Cleanup
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [user, resetInactivityTimer, logout]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    if (token) {
      // Check token expiry client-side
      let tokenExpired = false;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          tokenExpired = true;
        }
      } catch {
        tokenExpired = true;
      }

      if (!tokenExpired) {
        // Sync the upload cookie even when no refresh happens (e.g. hard reload)
        setUploadCookie(token);
      }
      
      if (tokenExpired && refreshToken) {
        // Access token expired but we have a refresh token — try to refresh
        try {
          const response = await import('../services/api').then(api => 
            api.default.post('/api/auth/refresh', { refresh_token: refreshToken })
          );
          if (response.data?.access_token) {
            localStorage.setItem('token', response.data.access_token);
            setUploadCookie(response.data.access_token);
            if (response.data?.refresh_token) {
              localStorage.setItem('refreshToken', response.data.refresh_token);
            }
            const userResp = await authAPI.getCurrentUser();
            setUser(userResp.data);
            localStorage.setItem('user', JSON.stringify(userResp.data));
            setLoading(false);
            return;
          }
        } catch {
          // Refresh failed — clear everything
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          clearUploadCookie();
          setLoading(false);
          return;
        }
      } else if (tokenExpired) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearUploadCookie();
        setLoading(false);
        return;
      }
      
      try {
        const response = await authAPI.getCurrentUser();
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        clearUploadCookie();
      }
    }
    setLoading(false);
  };

  const login = async (username, password, twoFactorToken, turnstileToken, deviceId) => {
    try {
      const response = await authAPI.login(username, password, twoFactorToken, turnstileToken, deviceId);
      const data = response.data;
      
      // Check if 2FA setup is required (admin/tenant-owner enforcement)
      if (data.requires_2fa_setup) {
        return { 
          success: false, 
          requires_2fa_setup: true,
          setup_token: data.setup_token,
          message: data.message
        };
      }
      
      // Check if email OTP is required (demo admin users)
      if (data.requires_email_otp) {
        return {
          success: false,
          requires_email_otp: true,
          username: data.username,
          signup_email: data.signup_email,
          password_verified_token: data.password_verified_token,
          message: data.message
        };
      }
      
      // Check if 2FA verification is required (existing 2FA users)
      if (data.requires_2fa) {
        return {
          success: false,
          requires_2fa: true,
          username: data.username,
          message: data.message
        };
      }

      // Check if password has expired (org security policy)
      if (data.password_expired) {
        return {
          success: false,
          password_expired: true,
          username: data.username,
          message: data.message,
          expiry_token: data.expiry_token
        };
      }

      // Check if new-device verification is required (unrecognized device)
      if (data.requires_device_verification) {
        return {
          success: false,
          requires_device_verification: true,
          username: data.username,
          device_token: data.device_token,
          device_brief: data.device_brief,
          message: data.message
        };
      }
      
      const { access_token, refresh_token, user } = data;
      localStorage.setItem('token', access_token);
      setUploadCookie(access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));  // Save user for session persistence
      
      // Use user data from login response (no need for second API call)
      setUser(user);
      
      return { success: true };
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      const isNetworkError = !error.response && !isTimeout;

      return { 
        success: false, 
        error: isTimeout
          ? 'Login is taking too long. Please try again.'
          : isNetworkError
            ? 'Cannot reach the server. Please confirm the backend is running and try again.'
            : error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const googleSignIn = async (credential) => {
    try {
      const response = await authAPI.googleSignIn(credential);
      const data = response.data;
      if (data.requires_2fa) {
        return {
          success: false,
          requires_2fa: true,
          pending_token: data.pending_token,
          username: data.username,
        };
      }
      const { access_token, refresh_token, user } = data;
      localStorage.setItem('token', access_token);
      setUploadCookie(access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Google sign-in failed'
      };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, googleSignIn, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
